import React from 'react';
import _ from 'lodash';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { useQuery } from '@tanstack/react-query';

import { Paper, useTheme, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { useNotifications } from '../../_lib/notifications/useNotifications';
import { fetchActivityAggregation } from '../../api/activity.api';
import { getData } from '../../utils/functional/fp';

export const ActivityBarChart = ({ q }: any) => {
  const classes = useStyles();
  const { palette } = useTheme();

  const activityEventsQuery = useActivityEventSeriesQuery(q);

  const options = React.useMemo(() => {
    return {
      chart: {
        type: 'column',
        height: '145',
        backgroundColor: palette.background.default,
      },
      yAxis: {
        title: {
          text: '',
        },
        lineColor: '#9192a4',
        lineWidth: 1,
        labels: {
          style: {
            color: '#9aa0ae',
            fontSize: '10px',
          },
        },
        gridLineColor: '#393941',
      },
      title: {
        text: '',
      },
      xAxis: {
        type: 'datetime',
        label: {
          style: {
            color: '#9aa0ae',
            fontSize: '8px',
          },
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0,
          borderWidth: 0.1,
        },
      },
      series: [
        {
          data: activityEventsQuery.data,
          name: 'Events',
          color: palette.primary.main,
        },
      ],
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      responsive: true,
    };
  }, [
    activityEventsQuery.data,
    palette.background.default,
    palette.primary.main,
  ]);

  return (
    <Paper className={classes.chart} elevation={6}>
      <Box style={{ minHeight: 145 }}>
        {options ? (
          <HighchartsReact highcharts={Highcharts} options={options} />
        ) : null}
      </Box>
    </Paper>
  );
};

const useStyles = makeStyles(() => ({
  chart: {
    width: '100%',
    height: '160px',
    padding: '0.5rem',
    marginBottom: '1rem',
  },
}));

const useActivityEventSeriesQuery = (query: any) => {
  const notifications = useNotifications();
  const params = _.pick(query, 'after', 'before');

  return useQuery(
    ['activity-events', { params }],
    () =>
      fetchActivityAggregation(params)
        .then(getData)
        .then((data: any) => {
          const agg = _.get(
            data,
            `aggregations['time range']['Event distribution by time'].buckets`,
            []
          );

          return agg.map((ev: any) => [Number(ev.key), ev.doc_count]);
        })
        .catch((error: any) => {
          if (error && error.message !== 'Request Cancelled') {
            notifications.show({
              type: 'error',
              title:
                'Something went wrong loading chart data, please try again in a few minutes',
            });
          }
        }),
    { keepPreviousData: true }
  );
};
